import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { storefrontApi } from '../lib/api'
import { formatMoney } from '../lib/format'
import { productImageUrl } from '../lib/images'
import { useCart } from '../context/CartContext'
import type { ProductDetail } from '../types/commerce'

export function Product() {
  const { slug = '' } = useParams()
  const [product, setProduct] = useState<ProductDetail | null>(null)
  const [error, setError] = useState('')
  const [color, setColor] = useState('')
  const [size, setSize] = useState('')
  const [activeImage, setActiveImage] = useState(0)
  const cart = useCart()

  useEffect(() => {
    storefrontApi.product(slug).then(data => {
      if (!data?.id) throw new Error('Produto não encontrado.')
      setProduct(data)
      const first = data.variants.find(v => v.available) ?? data.variants[0]
      if (first) { setColor(first.color_name); setSize(first.size) }
    }).catch(err => setError(err.message))
  }, [slug])

  const colors = useMemo(() => product ? [...new Map(product.variants.map(v => [v.color_name, v])).values()] : [], [product])
  const sizes = useMemo(() => product ? product.variants.filter(v => v.color_name === color) : [], [product, color])
  const selected = product?.variants.find(v => v.color_name === color && v.size === size)

  if (error) return <div className="page-message">{error}</div>
  if (!product) return <div className="page-message">Carregando produto...</div>

  const images = product.images.length ? product.images : [{ storage_path: '', alt_text: product.name, position: 0, is_primary: true }]
  return (
    <div className="product-page">
      <section className="product-gallery">
        <div className="product-gallery__main">
          {images[activeImage]?.storage_path ? <img src={productImageUrl(images[activeImage].storage_path)} alt={images[activeImage].alt_text ?? product.name} /> : <div className="image-placeholder">GK MAISON</div>}
        </div>
        {images.length > 1 && <div className="product-gallery__thumbs">{images.map((img, idx) => <button className={idx === activeImage ? 'is-active' : ''} key={`${img.storage_path}-${idx}`} onClick={() => setActiveImage(idx)}>{img.storage_path ? <img src={productImageUrl(img.storage_path)} alt="" /> : 'GK'}</button>)}</div>}
      </section>
      <section className="product-info">
        <span className="eyebrow">{product.category?.name ?? 'GK MAISON'}</span>
        <h1>{product.name}</h1>
        <div className="product-price">{product.sale_price != null ? <><span>{formatMoney(product.price)}</span><strong>{formatMoney(product.sale_price)}</strong></> : <strong>{formatMoney(product.price)}</strong>}</div>
        {product.short_description && <p className="product-lead">{product.short_description}</p>}

        {colors.length > 0 && <div className="selector-group"><label>COR · {color}</label><div className="color-options">{colors.map(v => <button key={v.color_name} className={color === v.color_name ? 'is-active' : ''} onClick={() => { setColor(v.color_name); const firstSize = product.variants.find(x => x.color_name === v.color_name && x.available) ?? product.variants.find(x => x.color_name === v.color_name); setSize(firstSize?.size ?? '') }}><span style={{ background: v.color_hex ?? '#ddd' }} />{v.color_name}</button>)}</div></div>}
        {sizes.length > 0 && <div className="selector-group"><label>TAMANHO</label><div className="size-options">{sizes.map(v => <button key={v.id} disabled={!v.available} className={size === v.size ? 'is-active' : ''} onClick={() => setSize(v.size)}>{v.size}</button>)}</div></div>}

        <div className="product-purchase"><button className="button button--dark button--full" disabled={!selected?.available} onClick={() => selected && cart.add(selected.id)}>{selected?.available ? 'ADICIONAR À SACOLA' : 'INDISPONÍVEL'}</button></div>

        <div className="product-details">
          {product.description && <details open><summary>DESCRIÇÃO</summary><p>{product.description}</p></details>}
          {product.composition && <details><summary>COMPOSIÇÃO</summary><p>{product.composition}</p></details>}
          {product.care_instructions && <details><summary>CUIDADOS</summary><p>{product.care_instructions}</p></details>}
          <details><summary>ENTREGA E DEVOLUÇÕES</summary><p>Prazo e disponibilidade são confirmados no fechamento do pedido.</p></details>
        </div>
      </section>
    </div>
  )
}
